<template>
  <div class="checkout-screen">
    <div class="flex flex-col w-full flex-grow pb-32 xl:pb-0 cart-clearance">
      <CheckoutHeader current-screen="checkout" />
      <section
        class="checkout-main"
        :aria-label="$gettext('Checkout')"
      >
        <div class="flex justify-center">
          <VPLoadingSpinner :loading="!formReady">
            <div class="lg:w-1/3 lg:min-w-[420px]">
              <CustomerInformationForm v-if="orderTotal === 0" />
              <StripePaymentForm
                v-else
                :quick-checkout-tos="event?.config?.quickCheckoutTos"
                show-agreement-checkboxes
                :payment-intent="props.paymentIntent"
                :payment-intent-secret="props.paymentIntentSecret"
                :order-fingerprint="props.orderFingerprint"
              />
            </div>
          </VPLoadingSpinner>
        </div>
      </section>
    </div>
    <section
      class="cart shopping-cart overflow-x-auto lg:overflow-x-hidden"
      :aria-label="$gettext('Cart')"
    >
      <VPLoadingSpinner :loading="!shoppingCartReady">
        <ShoppingCart
          v-if="event && shoppingCart"
          read-only
          show-reset
          :event="event"
          :items="shoppingCartItems"
          :price-breakdown="shoppingCart.priceBreakdown"
          :control-quantity="shoppingCart.controlQuantity"
          :mobile-next-label="$gettext('Show')"
          class="min-w-full"
        />
      </VPLoadingSpinner>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from './stores/checkout';
import { computed, onMounted } from 'vue';
import { clientOnly } from 'vike-vue/clientOnly';
import CustomerInformationForm from './CustomerInformationForm.vue';
import ShoppingCart from './ShoppingCart.vue';
import { VPLoadingSpinner } from '@vp/vue-components';
import { until } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';
import { navigate } from 'vike/client/router';
import CheckoutHeader from './CheckoutHeader.vue';
import { useTracking } from './components/tracking/useTracking';
import { eventTrackingCurrency } from './components/tracking/trackingCurrency';
import { cartTrackingItems } from './components/tracking/trackingItems';
import { Util } from '@vp/common';

const { LoadingState } = Util;

// StripePaymentForm uses browser-only integrations (Stripe, Hive) and calls
// useHive() in setup, which throws during SSR. Load it client-only. (Same guard
// as Checkout.vue.)
const StripePaymentForm = clientOnly(() => import('./StripePaymentForm.vue'));

type Props = {
  paymentIntent?: string;
  paymentIntentSecret?: string;
  orderFingerprint?: string;
};

const props = defineProps<Props>();

const router = useRouter();
const toast = useToast();
const { $gettext } = useGettext();
const { trackEvent } = useTracking();

const checkoutStore = useCheckoutStore();
checkoutStore.init();

const event = computed(() => checkoutStore.getSelectedEvent);
const shoppingCart = computed(() => checkoutStore.shoppingCart);

const shoppingCartLoading = computed(() => checkoutStore.shoppingCartLoadingState);
const shoppingCartItems = computed(() => checkoutStore.getCartedTickets.filter(({ count }) => (count ?? 0) > 0));
const priceBreakdown = computed(() => shoppingCart.value?.priceBreakdown);
const orderTotal = computed(() => priceBreakdown.value?.orderTotal || 0);

const shoppingCartReady = computed(() => shoppingCartLoading.value == LoadingState.DONE);

// `begin_checkout` prices its items from the blueprints, which `init()` loads *after*
// the cart that opens the gate below — so on a direct load of this page they are not
// there yet. Terminal state rather than DONE alone: a failed ticket load can fire the
// event immediately with the cart's own base prices instead of burning the wait first.
const ticketsSettled = computed(
  () => checkoutStore.ticketsLoading == LoadingState.DONE || checkoutStore.ticketsLoading == LoadingState.ERROR,
);
const formReady = computed(() => !!event.value && checkoutStore.getCartedTickets.length);

onMounted(async () => {
  await until(shoppingCartReady).toBe(true);
  // TODO: Fix. This is very broken
  if (!checkoutStore.hasValidLock) {
    // toast.error($gettext('You must lock tickets before payment!'));
    toast.error($gettext('Please click empty cart, refresh the page, and try again!'));
    navigate(router.resolve({ name: 'OrderNew' }).href);
  } else {
    // Bounded: a ticket load that never settles delays the event, it never drops it.
    await until(ticketsSettled).toBe(true, { timeout: 5000, throwOnTimeout: false });

    trackEvent('begin_checkout', {
      currency: eventTrackingCurrency(event.value),
      value: orderTotal.value,
      items: cartTrackingItems(shoppingCartItems.value, checkoutStore.selectedEventTickets),
    });
  }
});
</script>
